import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeInfo } from "lucide-react";
import { AuthShell, Field, inputCls, submitCls } from "@/components/AuthShell";
import { api, errMsg, LEVELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function Register() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", username: "", email: "", phone: "", password: "", level: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/register", { ...form, username: form.username.trim().toLowerCase(), level: Number(form.level) });
      setUser(data);
      toast.success(t.auth.registered);
      nav("/library");
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 409 ? t.auth.usernameTaken : s === 400 ? t.auth.emailTaken : s === 422 ? t.auth.invalid : errMsg(err) || t.auth.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t.auth.registerTitle} subtitle={t.auth.registerSub}
      footer={<>{t.auth.haveAccount} <Link to="/login" className="font-semibold text-[#8B3A2B] underline-offset-4 hover:underline" data-testid="register-to-login-link">{t.auth.signIn}</Link></>}>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <p className="flex items-start gap-2 rounded-sm border-2 border-[#B45309] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#7C2D12]" data-testid="register-subscription-note">
          <BadgeInfo className="w-5 h-5 shrink-0 mt-0.5" /> {t.auth.subNote}
        </p>
        <Field label={t.auth.name}>
          <input required minLength={2} className={inputCls} value={form.name} onChange={set("name")} data-testid="register-name-input" />
        </Field>
        <Field label={t.auth.usernameHint}>
          <input required pattern="[A-Za-z0-9._\-]{3,30}" dir="ltr" autoCapitalize="off" autoCorrect="off" autoComplete="username" className={inputCls} value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })} data-testid="register-username-input" />
        </Field>
        <Field label={t.auth.email}>
          <input type="email" required dir="ltr" className={inputCls} value={form.email} onChange={set("email")} data-testid="register-email-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t.auth.phone}>
            <input dir="ltr" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="register-phone-input" />
          </Field>
          <Field label={t.auth.level}>
            <select className={inputCls} value={form.level} onChange={set("level")} data-testid="register-level-select">
              {LEVELS.map((l) => <option key={l.n} value={l.n}>{l.n} · {t.levels[l.n]}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t.auth.passwordHint}>
          <input type="password" required minLength={6} dir="ltr" className={inputCls} value={form.password} onChange={set("password")} data-testid="register-password-input" />
        </Field>
        {error && <p className="text-sm text-red-800 bg-red-50 border border-red-200 p-3" data-testid="register-error">{error}</p>}
        <button disabled={loading} className={submitCls} data-testid="auth-register-submit">{loading ? t.auth.registering : t.auth.registerBtn}</button>
      </form>
    </AuthShell>
  );
}
